import { useEffect, useState } from 'react';
import { EngineTuningSection } from '../help/EngineTuningSection';
import { Padel24Engine } from '../../engine/platformEngine';
import { loadEngineSettings } from '../../lib/api/engineSettings';

// ─────────────────────────────────────────────────────────────────────
// ENGINE SETTINGS, in the platform panel (#243).
//
// These are PLATFORM-GLOBAL: one change re-tunes how ELO is calculated for
// every club on the platform. The CTO confirmed they are not club
// configuration, so the editing surface belongs here rather than in a club's
// own settings — and the server refuses the write to anyone without
// PLATFORM_SUPERADMIN regardless of what this page renders.
//
// IT LOADS BEFORE IT OFFERS TO EDIT, and that is not cosmetic. The engine
// starts from its compiled defaults; saving before the stored row has been
// read would write those defaults over whatever is actually configured — a
// silent reset of every club's ELO tuning, performed by opening a page and
// pressing save.
// ─────────────────────────────────────────────────────────────────────

export interface EngineSettingsSectionProps {
  /** Does the viewer hold `engine.settings.manage`, per the SERVER's answer (#924)? */
  canWrite: boolean;
}

type State = { status: 'loading' } | { status: 'ready' } | { status: 'error'; message: string };

export function EngineSettingsSection({ canWrite }: EngineSettingsSectionProps) {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = await loadEngineSettings();
        if (cancelled) return;
        // A missing row is a legitimate answer: nothing has been configured,
        // so the compiled defaults ARE the current settings.
        if (saved) Padel24Engine.settings.apply(saved);
        setState({ status: 'ready' });
      } catch (e) {
        if (cancelled) return;
        setState({
          status: 'error',
          message: e instanceof Error ? e.message : 'Ukjent feil',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="card p-6 mt-6" data-testid="sa-engine-settings">
      <h2 className="font-display text-sm font-semibold">ELO-motor</h2>
      <p className="text-[11px] text-ink-3 mt-1">
        Plattform-innstillinger. Endringer gjelder alle klubber.
      </p>

      {state.status === 'loading' && (
        <p className="text-[11px] text-ink-3 mt-3" data-testid="sa-engine-loading">
          Henter innstillinger…
        </p>
      )}

      {state.status === 'error' && (
        <p className="text-[11px] text-red-400 mt-3" data-testid="sa-engine-error">
          Kunne ikke hente innstillingene ({state.message}). Last siden på nytt før du endrer noe.
        </p>
      )}

      {/* Editing is offered only once the stored values are in hand — see the
          header. Until then the panel is not rendered at all, so there is
          nothing to save defaults over. */}
      {state.status === 'ready' && (
        <div className="mt-3">
          <EngineTuningSection Padel24Engine={Padel24Engine} canWrite={canWrite} />
          {!canWrite && (
            <p className="text-[11px] text-ink-3 mt-2" data-testid="sa-engine-readonly">
              Du har lesetilgang. Kun superadmin kan endre motorinnstillinger.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
